import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { 
  ArrowLeft, User, MapPin, Plus, Check, ChevronRight, Calendar, 
  Clock, DollarSign, Search, Home, Sparkles, X, Save, Zap,
  ChevronLeft, AlertTriangle
} from 'lucide-react';
import { formatFullName, formatCurrency } from '../utils/helpers';
import { cleaningFrequencies } from '../utils/estimateData';
import { calculateBasePrice, formatPrice, FREQUENCY_LABELS } from '../utils/pricingCalculator';
import Modal from '../components/Modal';
import { format, addDays, startOfWeek, addWeeks, subWeeks, parseISO, isSameDay } from 'date-fns';
import './QuickAddJob.css';

function QuickAddJob() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { 
    state, getClientById, getClientProperties, addJob, createEstimate, 
    updateClient, generateId, addClient, addProperty, findDuplicateClients 
  } = useApp();
  const { clients, employees } = state;

  // Pre-selected client from URL
  const preSelectedClientId = searchParams.get('clientId');

  // Flow step - now starts with 'date'
  const [step, setStep] = useState('date');
  
  // Calendar state for date picker
  const [calendarWeekStart, setCalendarWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 0 }));
  
  // Selection state
  const [selectedClientId, setSelectedClientId] = useState(preSelectedClientId || '');
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [showNewPropertyForm, setShowNewPropertyForm] = useState(false);
  const [clientSearch, setClientSearch] = useState('');

  // Job details
  const [jobDetails, setJobDetails] = useState({
    date: new Date().toISOString().split('T')[0],
    scheduledTime: '09:00',
    duration: 2.5,
    serviceType: 'biweekly',
    price: 185,
    isDeepClean: false,
    deepCleanPrice: 0,
    team: [],
    notes: '',
    createEstimate: true // Auto-create estimate for reference
  });

  // Custom line items
  const [lineItems, setLineItems] = useState([
    { id: 'base', label: 'Cleaning Service', price: 185, required: false }
  ]);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({ label: '', price: '' });

  // New client form state
  const [newClient, setNewClient] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  // New property form state
  const [newProperty, setNewProperty] = useState({
    addressLine1: '',
    addressLine2: '',
    city: 'Charlottesville',
    state: 'VA',
    zip: '',
    sqft: '',
    bedrooms: '',
    bathrooms: ''
  });

  // Duplicate client warning state
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateClients, setDuplicateClients] = useState([]);
  const [pendingClientData, setPendingClientData] = useState(null);

  // Get selected client and their properties
  const selectedClient = selectedClientId ? getClientById(selectedClientId) : null;
  const clientProperties = selectedClientId ? getClientProperties(selectedClientId) : [];
  const selectedProperty = selectedPropertyId ? state.properties.find(p => p.id === selectedPropertyId) : null;

  // Filter clients for search
  const filteredClients = clients.filter(client => {
    if (!clientSearch) return true;
    const name = `${client.firstName} ${client.lastName}`.toLowerCase();
    const email = (client.email || '').toLowerCase();
    const phone = (client.phone || '').toLowerCase();
    const search = clientSearch.toLowerCase();
    return name.includes(search) || email.includes(search) || phone.includes(search);
  });

  // Get team members eligible for dispatch (active and onboarding)
  const activeEmployees = useMemo(() => {
    return employees.filter(e => e.status === 'active' || e.status === 'onboarding');
  }, [employees]);

  // Calculate total from line items
  const totalPrice = useMemo(() => {
    return lineItems.reduce((sum, item) => sum + (item.price || 0), 0);
  }, [lineItems]);

  // Update job price when line items change
  useEffect(() => {
    setJobDetails(prev => ({ ...prev, price: totalPrice }));
  }, [totalPrice]);

  // Auto-calculate based on property if available
  useEffect(() => {
    if (selectedProperty && selectedProperty.sqft) {
      try {
        const calc = calculateBasePrice({
          sqft: selectedProperty.sqft,
          condition: 'lived_in',
          frequency: jobDetails.serviceType || 'biweekly',
          bedrooms: selectedProperty.bedrooms || 3,
          fullBaths: Math.floor(selectedProperty.bathrooms || 2),
          halfBaths: 0,
          people: 3,
          sheddingPets: 0,
          glassShowerDoors: 0
        });
        
        if (calc && calc.subtotal) {
          setLineItems(prev => {
            // Keep any non-base items, update the base item
            const otherItems = prev.filter(item => item.id !== 'base');
            return [
              { 
                id: 'base', 
                label: `${FREQUENCY_LABELS[jobDetails.serviceType] || 'Cleaning'} Service`,
                description: `${selectedProperty.sqft.toLocaleString()} sqft`,
                price: Math.round(calc.subtotal), 
                required: false 
              },
              ...otherItems
            ];
          });
        }
      } catch (e) {
        console.error('Error calculating price:', e);
      }
    } else {
      // No property with sqft - just update the label based on service type
      setLineItems(prev => {
        return prev.map(item => {
          if (item.id === 'base') {
            return {
              ...item,
              label: `${FREQUENCY_LABELS[jobDetails.serviceType] || 'Cleaning'} Service`
            };
          }
          return item;
        });
      });
    }
  }, [selectedProperty, jobDetails.serviceType]);

  // Handle client selection
  const handleSelectClient = (clientId) => {
    setSelectedClientId(clientId);
    const clientProps = getClientProperties(clientId);
    
    // Auto-select primary property or first property if only one
    if (clientProps.length > 0) {
      const primaryProp = clientProps.find(p => p.isPrimary) || clientProps[0];
      setSelectedPropertyId(primaryProp.id);
    } else {
      setSelectedPropertyId('');
    }
    
    // Go directly to details step (property is now a dropdown there)
    setStep('details');
  };

  // Handle new client creation (with property if provided in inline form)
  const handleCreateClient = async (e) => {
    e.preventDefault();
    
    // Check for duplicate clients
    const duplicates = findDuplicateClients(newClient);
    
    if (duplicates.length > 0) {
      // Store pending data and show warning
      setPendingClientData({
        ...newClient,
        status: 'active'
      });
      setDuplicateClients(duplicates);
      setShowDuplicateWarning(true);
      return;
    }
    
    // No duplicates, proceed with creation
    await createNewClient(newClient);
  };

  // Actually create the client (called after duplicate check)
  const createNewClient = async (clientData) => {
    const client = await addClient({
      ...clientData,
      status: 'active' // Directly make them active since we're adding a job
    });
    setSelectedClientId(client.id);
    
    // If property info was provided in the inline form, create it too
    if (newProperty.addressLine1 && newProperty.city && newProperty.zip) {
      const property = await addProperty({
        ...newProperty,
        clientId: client.id,
        sqft: newProperty.sqft ? parseInt(newProperty.sqft) : null,
        bedrooms: newProperty.bedrooms ? parseInt(newProperty.bedrooms) : null,
        bathrooms: newProperty.bathrooms ? parseFloat(newProperty.bathrooms) : null,
        isPrimary: true
      });
      setSelectedPropertyId(property.id);
      setShowNewClientForm(false);
      setStep('details'); // Skip property step since we just created one
    } else {
      // No property info - go to property step
      setShowNewClientForm(false);
      setShowNewPropertyForm(true);
      setStep('property');
    }
    
    // Reset the new client form
    setNewClient({
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    });
  };

  // Handle using existing client from duplicate warning
  const handleUseExistingClient = (existingClient) => {
    setSelectedClientId(existingClient.id);
    setShowDuplicateWarning(false);
    setShowNewClientForm(false);
    setPendingClientData(null);
    setDuplicateClients([]);
    
    const clientProps = getClientProperties(existingClient.id);
    if (clientProps.length > 0) {
      setStep('property');
      if (clientProps.length === 1) {
        setSelectedPropertyId(clientProps[0].id);
      }
    } else {
      setShowNewPropertyForm(true);
      setStep('property');
    }
    
    // Reset the new client form
    setNewClient({
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    });
  };

  // Handle creating new client anyway despite duplicates
  const handleCreateAnyway = async () => {
    setShowDuplicateWarning(false);
    if (pendingClientData) {
      await createNewClient(pendingClientData);
    }
    setPendingClientData(null);
    setDuplicateClients([]);
  };

  // Handle property selection
  const handleSelectProperty = (propertyId) => {
    setSelectedPropertyId(propertyId);
    setStep('details');
  };

  // Handle new property creation
  const handleCreateProperty = async (e) => {
    e.preventDefault();
    const property = await addProperty({
      ...newProperty,
      clientId: selectedClientId,
      sqft: newProperty.sqft ? parseInt(newProperty.sqft) : null,
      bedrooms: newProperty.bedrooms ? parseInt(newProperty.bedrooms) : null,
      bathrooms: newProperty.bathrooms ? parseFloat(newProperty.bathrooms) : null,
      isPrimary: clientProperties.length === 0
    });
    setSelectedPropertyId(property.id);
    setShowNewPropertyForm(false);
    setStep('details');
    
    // Reset the property form for next use
    setNewProperty({
      addressLine1: '',
      addressLine2: '',
      city: 'Charlottesville',
      state: 'VA',
      zip: '',
      sqft: '',
      bedrooms: '',
      bathrooms: ''
    });
  };

  // Add custom line item
  const handleAddLineItem = () => {
    if (!newItem.label || !newItem.price) return;
    setLineItems(prev => [...prev, {
      id: generateId('li'),
      label: newItem.label,
      price: parseFloat(newItem.price),
      required: false
    }]);
    setNewItem({ label: '', price: '' });
    setShowAddItem(false);
  };

  // Remove line item
  const removeLineItem = (id) => {
    setLineItems(prev => prev.filter(item => item.id !== id));
  };

  // Update line item price
  const updateLineItemPrice = (id, price) => {
    setLineItems(prev => prev.map(item => 
      item.id === id ? { ...item, price: parseFloat(price) || 0 } : item
    ));
  };

  // Toggle team member
  const toggleTeamMember = (empId) => {
    setJobDetails(prev => ({
      ...prev,
      team: prev.team.includes(empId) 
        ? prev.team.filter(id => id !== empId)
        : [...prev.team, empId]
    }));
  };

  // Calculate end time
  const calculateEndTime = (startTime, duration) => {
    if (!startTime) return '';
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + (duration * 60);
    const endHours = Math.floor(totalMinutes / 60);
    const endMins = Math.round(totalMinutes % 60);
    return `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
  };

  // Handle save job
  const handleSaveJob = async () => {
    if (!selectedClientId || !selectedPropertyId) return;

    const client = getClientById(selectedClientId);
    const property = state.properties.find(p => p.id === selectedPropertyId);
    const teamNames = activeEmployees
      .filter(e => jobDetails.team.includes(e.id))
      .map(e => `${e.firstName} ${e.lastName.charAt(0)}.`);

    // Build job data
    const jobData = {
      clientId: selectedClientId,
      propertyId: selectedPropertyId,
      clientName: client ? `${client.firstName} ${client.lastName}` : 'Unknown',
      address: property?.addressLine1 || '',
      city: property?.city || 'Charlottesville',
      state: property?.state || 'VA',
      zip: property?.zip || '',
      fullAddress: property ? `${property.addressLine1}, ${property.city}, ${property.state} ${property.zip}` : '',
      date: jobDetails.date,
      scheduledTime: jobDetails.scheduledTime,
      scheduledEndTime: calculateEndTime(jobDetails.scheduledTime, jobDetails.duration),
      duration: jobDetails.duration,
      serviceType: jobDetails.serviceType,
      frequency: jobDetails.serviceType,
      price: totalPrice,
      status: 'scheduled',
      team: jobDetails.team,
      teamNames,
      notes: jobDetails.notes,
      isFirstClean: jobDetails.isDeepClean,
      lineItems,
      // Property details
      sqft: property?.sqft || null,
      bedrooms: property?.bedrooms || null,
      bathrooms: property?.bathrooms || null,
      clientPhone: client?.phone || '',
      clientEmail: client?.email || '',
      accessInstructions: property?.accessInstructions || '',
      source: 'quick_add'
    };

    // Create the job
    const newJob = await addJob(jobData);

    // Optionally create a reference estimate
    if (jobDetails.createEstimate) {
      const estimateData = {
        clientId: selectedClientId,
        propertyId: selectedPropertyId,
        calculatorInputs: {
          sqft: property?.sqft || 2000,
          condition: 'lived_in',
          frequency: jobDetails.serviceType,
          bedrooms: property?.bedrooms || 3,
          fullBaths: Math.floor(property?.bathrooms || 2),
          halfBaths: 0,
          people: 3,
          sheddingPets: 0,
          glassShowerDoors: 0
        },
        selectedFrequencies: [jobDetails.serviceType],
        frequencyOptions: [{
          value: jobDetails.serviceType,
          label: cleaningFrequencies[jobDetails.serviceType]?.label || jobDetails.serviceType,
          price: totalPrice
        }],
        lineItems,
        deepCleanLineItems: jobDetails.isDeepClean ? lineItems : [],
        recurringTotal: totalPrice,
        deepCleanTotal: jobDetails.isDeepClean ? totalPrice : 0,
        skipDeepClean: !jobDetails.isDeepClean,
        status: 'approved',
        approvedAt: new Date().toISOString(),
        approvedRecurringTotal: totalPrice,
        approvedDeepCleanTotal: jobDetails.isDeepClean ? totalPrice : 0,
        approvedBy: 'quick_add',
        convertedToJobId: newJob.id,
        internalNotes: `Quick add job - auto-created estimate for reference. ${jobDetails.notes}`
      };

      await createEstimate(estimateData);
    }

    // Update client status if needed
    if (client && client.status === 'lead') {
      await updateClient(client.id, { status: 'active', frequency: jobDetails.serviceType });
    }

    // Navigate to dispatch
    navigate('/admin/dispatch');
  };

  return (
    <div className="quick-add-job-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/admin/dispatch')}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1><Zap size={24} /> Quick Add Job</h1>
            <p>Add a job directly to dispatch - for existing clients or quick imports</p>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="progress-steps">
        <div className={`progress-step ${step === 'date' ? 'active' : step !== 'date' ? 'completed' : ''}`}>
          <div className="step-icon">
            {step !== 'date' ? <Check size={16} /> : <Calendar size={16} />}
          </div>
          <span>Date</span>
        </div>
        <div className="step-connector" />
        <div className={`progress-step ${step === 'client' ? 'active' : selectedClientId ? 'completed' : ''}`}>
          <div className="step-icon">
            {selectedClientId ? <Check size={16} /> : <User size={16} />}
          </div>
          <span>Client</span>
        </div>
        <div className="step-connector" />
        <div className={`progress-step ${step === 'property' ? 'active' : selectedPropertyId ? 'completed' : ''}`}>
          <div className="step-icon">
            {selectedPropertyId ? <Check size={16} /> : <Home size={16} />}
          </div>
          <span>Property</span>
        </div>
        <div className="step-connector" />
        <div className={`progress-step ${step === 'details' ? 'active' : ''}`}>
          <div className="step-icon"><Clock size={16} /></div>
          <span>Details</span>
        </div>
      </div>

      {/* Step 0: Date Selection */}
      {step === 'date' && (
        <div className="step-content">
          <div className="section-card date-picker-card">
            <div className="section-header">
              <h2><Calendar size={20} /> Select Date</h2>
            </div>
            
            <div className="calendar-nav">
              <button className="btn btn-icon" onClick={() => setCalendarWeekStart(subWeeks(calendarWeekStart, 1))}>
                <ChevronLeft size={20} />
              </button>
              <span className="calendar-month">{format(calendarWeekStart, 'MMMM yyyy')}</span>
              <button className="btn btn-icon" onClick={() => setCalendarWeekStart(addWeeks(calendarWeekStart, 1))}>
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="calendar-grid">
              {[0, 1, 2, 3, 4, 5, 6].map(dayOffset => {
                const date = addDays(calendarWeekStart, dayOffset);
                const dateStr = format(date, 'yyyy-MM-dd');
                const isSelected = jobDetails.date === dateStr;
                const isToday = isSameDay(date, new Date());
                
                return (
                  <button
                    key={dayOffset}
                    className={`calendar-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
                    onClick={() => {
                      setJobDetails(prev => ({ ...prev, date: dateStr }));
                    }}
                  >
                    <span className="day-name">{format(date, 'EEE')}</span>
                    <span className="day-number">{format(date, 'd')}</span>
                    {isToday && <span className="today-label">Today</span>}
                  </button>
                );
              })}
            </div>

            <div className="quick-dates">
              <button 
                className="quick-date-btn"
                onClick={() => {
                  const today = format(new Date(), 'yyyy-MM-dd');
                  setJobDetails(prev => ({ ...prev, date: today }));
                  setCalendarWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }));
                }}
              >
                Today
              </button>
              <button 
                className="quick-date-btn"
                onClick={() => {
                  const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');
                  setJobDetails(prev => ({ ...prev, date: tomorrow }));
                  setCalendarWeekStart(startOfWeek(addDays(new Date(), 1), { weekStartsOn: 0 }));
                }}
              >
                Tomorrow
              </button>
              <button 
                className="quick-date-btn"
                onClick={() => {
                  const nextWeek = addDays(new Date(), 7);
                  setJobDetails(prev => ({ ...prev, date: format(nextWeek, 'yyyy-MM-dd') }));
                  setCalendarWeekStart(startOfWeek(nextWeek, { weekStartsOn: 0 }));
                }}
              >
                Next Week
              </button>
            </div>

            <div className="selected-date-display">
              <Calendar size={18} />
              <span>{format(parseISO(jobDetails.date), 'EEEE, MMMM d, yyyy')}</span>
            </div>

            <div className="step-actions">
              <button 
                className="btn btn-primary btn-lg"
                onClick={() => setStep(preSelectedClientId ? 'property' : 'client')}
              >
                Continue <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Client Selection */}
      {step === 'client' && !showNewClientForm && (
        <div className="step-content">
          <div className="section-card">
            <div className="section-header">
              <h2>Select Client</h2>
              <button className="btn btn-primary btn-sm" onClick={() => setShowNewClientForm(true)}>
                <Plus size={16} /> New Client
              </button>
            </div>

            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                autoFocus
              />
            </div>

            <div className="selection-list">
              {filteredClients.map(client => (
                <button
                  key={client.id}
                  className="selection-item"
                  onClick={() => handleSelectClient(client.id)}
                >
                  <div className="item-icon">
                    <User size={18} />
                  </div>
                  <div className="item-info">
                    <span className="item-name">{formatFullName(client)}</span>
                    <span className="item-meta">
                      {client.email}
                      {client.phone && ` • ${client.phone}`}
                    </span>
                  </div>
                  {client.status && (
                    <span className={`status-badge ${client.status}`}>{client.status}</span>
                  )}
                  <ChevronRight size={18} className="item-arrow" />
                </button>
              ))}
              
              {filteredClients.length === 0 && (
                <div className="no-results">
                  <p>No clients found</p>
                  <button className="btn btn-primary" onClick={() => setShowNewClientForm(true)}>
                    <Plus size={16} /> Add New Client
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 1b: Inline New Client Form */}
      {step === 'client' && showNewClientForm && (
        <div className="step-content">
          <div className="section-card">
            <div className="section-header">
              <h2><Plus size={20} /> New Client</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowNewClientForm(false)}>
                <X size={16} /> Back to Search
              </button>
            </div>

            <form onSubmit={handleCreateClient} className="inline-client-form">
              <div className="form-section-title">Client Information</div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={newClient.firstName}
                    onChange={(e) => setNewClient({...newClient, firstName: e.target.value})}
                    required
                    placeholder="Jane"
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={newClient.lastName}
                    onChange={(e) => setNewClient({...newClient, lastName: e.target.value})}
                    required
                    placeholder="Smith"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                    placeholder="jane@email.com"
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newClient.phone}
                    onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                    placeholder="(434) 555-1234"
                  />
                </div>
              </div>

              <div className="form-divider"></div>
              <div className="form-section-title">Property Address</div>
              
              <div className="form-group">
                <label>Street Address *</label>
                <input
                  type="text"
                  value={newProperty.addressLine1}
                  onChange={(e) => setNewProperty({...newProperty, addressLine1: e.target.value})}
                  required
                  placeholder="123 Main Street"
                />
              </div>
              
              <div className="form-group">
                <label>Apt/Suite</label>
                <input
                  type="text"
                  value={newProperty.addressLine2}
                  onChange={(e) => setNewProperty({...newProperty, addressLine2: e.target.value})}
                  placeholder="Apt 2B"
                />
              </div>

              <div className="form-row thirds">
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    value={newProperty.city}
                    onChange={(e) => setNewProperty({...newProperty, city: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={newProperty.state}
                    onChange={(e) => setNewProperty({...newProperty, state: e.target.value})}
                    maxLength={2}
                  />
                </div>
                <div className="form-group">
                  <label>ZIP *</label>
                  <input
                    type="text"
                    value={newProperty.zip}
                    onChange={(e) => setNewProperty({...newProperty, zip: e.target.value})}
                    required
                    placeholder="22901"
                  />
                </div>
              </div>

              <div className="form-row thirds">
                <div className="form-group">
                  <label>Square Feet</label>
                  <input
                    type="number"
                    value={newProperty.sqft}
                    onChange={(e) => setNewProperty({...newProperty, sqft: e.target.value})}
                    placeholder="2200"
                  />
                </div>
                <div className="form-group">
                  <label>Bedrooms</label>
                  <input
                    type="number"
                    value={newProperty.bedrooms}
                    onChange={(e) => setNewProperty({...newProperty, bedrooms: e.target.value})}
                    placeholder="3"
                  />
                </div>
                <div className="form-group">
                  <label>Bathrooms</label>
                  <input
                    type="number"
                    value={newProperty.bathrooms}
                    onChange={(e) => setNewProperty({...newProperty, bathrooms: e.target.value})}
                    step="0.5"
                    placeholder="2.5"
                  />
                </div>
              </div>

              <div className="step-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowNewClientForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-lg">
                  Create Client & Continue <ChevronRight size={18} />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Step 2: Property Selection */}
      {step === 'property' && !showNewPropertyForm && (
        <div className="step-content">
          <div className="section-card">
            <div className="section-header">
              <div className="selected-summary-mini">
                <User size={16} />
                <span>{formatFullName(selectedClient)}</span>
                <button className="btn-link" onClick={() => { setStep('client'); setSelectedClientId(''); }}>Change</button>
              </div>
              <button className="btn btn-primary btn-sm" onClick={() => setShowNewPropertyForm(true)}>
                <Plus size={16} /> New Address
              </button>
            </div>

            <h2>Select Property</h2>

            {clientProperties.length > 0 ? (
              <div className="selection-list">
                {clientProperties.map(property => (
                  <button
                    key={property.id}
                    className={`selection-item ${selectedPropertyId === property.id ? 'selected' : ''}`}
                    onClick={() => handleSelectProperty(property.id)}
                  >
                    <div className="item-icon">
                      <MapPin size={18} />
                    </div>
                    <div className="item-info">
                      <span className="item-name">{property.addressLine1}</span>
                      <span className="item-meta">
                        {property.city}, {property.state} {property.zip}
                        {property.sqft && ` • ${property.sqft.toLocaleString()} sqft`}
                        {property.bedrooms && ` • ${property.bedrooms}BR/${property.bathrooms}BA`}
                      </span>
                    </div>
                    {property.isPrimary && <span className="primary-badge">Primary</span>}
                    <ChevronRight size={18} className="item-arrow" />
                  </button>
                ))}
                
                {/* Add new property option at bottom of list */}
                <button
                  className="selection-item add-new-item"
                  onClick={() => setShowNewPropertyForm(true)}
                >
                  <div className="item-icon add-icon">
                    <Plus size={18} />
                  </div>
                  <div className="item-info">
                    <span className="item-name">Add New Address</span>
                    <span className="item-meta">Create a new property for this client</span>
                  </div>
                  <ChevronRight size={18} className="item-arrow" />
                </button>
              </div>
            ) : (
              <div className="no-results">
                <p>No properties yet for this client</p>
                <button className="btn btn-primary" onClick={() => setShowNewPropertyForm(true)}>
                  <Plus size={16} /> Add Property
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 2b: Inline New Property Form */}
      {step === 'property' && showNewPropertyForm && (
        <div className="step-content">
          <div className="section-card">
            <div className="section-header">
              <div className="selected-summary-mini">
                <User size={16} />
                <span>{formatFullName(selectedClient)}</span>
              </div>
              {clientProperties.length > 0 && (
                <button className="btn btn-secondary btn-sm" onClick={() => setShowNewPropertyForm(false)}>
                  <X size={16} /> Back to Properties
                </button>
              )}
            </div>

            <h2><Plus size={20} /> New Property</h2>

            <form onSubmit={handleCreateProperty} className="inline-client-form">
              <div className="form-group">
                <label>Street Address *</label>
                <input
                  type="text"
                  value={newProperty.addressLine1}
                  onChange={(e) => setNewProperty({...newProperty, addressLine1: e.target.value})}
                  required
                  placeholder="123 Main Street"
                  autoFocus
                />
              </div>
              
              <div className="form-group">
                <label>Apt/Suite</label>
                <input
                  type="text"
                  value={newProperty.addressLine2}
                  onChange={(e) => setNewProperty({...newProperty, addressLine2: e.target.value})}
                  placeholder="Apt 2B"
                />
              </div>

              <div className="form-row thirds">
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    value={newProperty.city}
                    onChange={(e) => setNewProperty({...newProperty, city: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={newProperty.state}
                    onChange={(e) => setNewProperty({...newProperty, state: e.target.value})}
                    maxLength={2}
                  />
                </div>
                <div className="form-group">
                  <label>ZIP *</label>
                  <input
                    type="text"
                    value={newProperty.zip}
                    onChange={(e) => setNewProperty({...newProperty, zip: e.target.value})}
                    required
                    placeholder="22901"
                  />
                </div>
              </div>

              <div className="form-divider"></div>
              <div className="form-section-title">Property Details (Optional)</div>

              <div className="form-row thirds">
                <div className="form-group">
                  <label>Square Feet</label>
                  <input
                    type="number"
                    value={newProperty.sqft}
                    onChange={(e) => setNewProperty({...newProperty, sqft: e.target.value})}
                    placeholder="2200"
                  />
                </div>
                <div className="form-group">
                  <label>Bedrooms</label>
                  <input
                    type="number"
                    value={newProperty.bedrooms}
                    onChange={(e) => setNewProperty({...newProperty, bedrooms: e.target.value})}
                    placeholder="3"
                  />
                </div>
                <div className="form-group">
                  <label>Bathrooms</label>
                  <input
                    type="number"
                    value={newProperty.bathrooms}
                    onChange={(e) => setNewProperty({...newProperty, bathrooms: e.target.value})}
                    step="0.5"
                    placeholder="2.5"
                  />
                </div>
              </div>

              <div className="step-actions">
                {clientProperties.length > 0 ? (
                  <button type="button" className="btn btn-secondary" onClick={() => setShowNewPropertyForm(false)}>
                    Cancel
                  </button>
                ) : (
                  <button type="button" className="btn btn-secondary" onClick={() => { setStep('client'); setSelectedClientId(''); }}>
                    Back
                  </button>
                )}
                <button type="submit" className="btn btn-primary btn-lg">
                  Add Property & Continue <ChevronRight size={18} />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Step 3: Job Details */}
      {step === 'details' && (
        <div className="step-content">
          {/* Summary Header */}
          <div className="selected-summary">
            <div className="summary-item">
              <User size={16} />
              <span>{formatFullName(selectedClient)}</span>
              <button className="btn-link-sm" onClick={() => setStep('client')}>Change</button>
            </div>
          </div>

          <div className="details-grid">
            {/* Property Selection Card */}
            <div className="section-card full-width">
              <h3><MapPin size={18} /> Property</h3>
              
              <div className="property-select-row">
                <select
                  className="property-dropdown"
                  value={selectedPropertyId}
                  onChange={(e) => {
                    if (e.target.value === 'new') {
                      setShowNewPropertyForm(true);
                      setStep('property');
                    } else {
                      setSelectedPropertyId(e.target.value);
                    }
                  }}
                >
                  <option value="">-- Select Property --</option>
                  {clientProperties.map(property => (
                    <option key={property.id} value={property.id}>
                      {property.addressLine1}, {property.city} {property.state}
                      {property.sqft ? ` (${property.sqft.toLocaleString()} sqft)` : ''}
                      {property.isPrimary ? ' ★' : ''}
                    </option>
                  ))}
                  <option value="new">+ Add New Property</option>
                </select>
                
                {selectedProperty && (
                  <div className="selected-property-details">
                    {selectedProperty.bedrooms && (
                      <span className="property-detail">{selectedProperty.bedrooms} BR / {selectedProperty.bathrooms} BA</span>
                    )}
                    {selectedProperty.sqft && (
                      <span className="property-detail">{selectedProperty.sqft.toLocaleString()} sqft</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Schedule Card */}
            <div className="section-card">
              <h3><Calendar size={18} /> Schedule</h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    value={jobDetails.date}
                    onChange={(e) => setJobDetails(prev => ({...prev, date: e.target.value}))}
                  />
                </div>
                
                <div className="form-group">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={jobDetails.scheduledTime}
                    onChange={(e) => setJobDetails(prev => ({...prev, scheduledTime: e.target.value}))}
                  />
                </div>
                
                <div className="form-group">
                  <label>Duration</label>
                  <select
                    value={jobDetails.duration}
                    onChange={(e) => setJobDetails(prev => ({...prev, duration: parseFloat(e.target.value)}))}
                  >
                    <option value="1">1 hour</option>
                    <option value="1.5">1.5 hours</option>
                    <option value="2">2 hours</option>
                    <option value="2.5">2.5 hours</option>
                    <option value="3">3 hours</option>
                    <option value="3.5">3.5 hours</option>
                    <option value="4">4 hours</option>
                    <option value="4.5">4.5 hours</option>
                    <option value="5">5 hours</option>
                    <option value="6">6 hours</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Service Type</label>
                  <select
                    value={jobDetails.serviceType}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setJobDetails(prev => ({...prev, serviceType: newType}));
                    }}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="one_time">One-Time</option>
                    <option value="move_in_out">Move In/Move Out</option>
                  </select>
                </div>
              </div>

              <div className="checkbox-option">
                <label>
                  <input
                    type="checkbox"
                    checked={jobDetails.isDeepClean}
                    onChange={(e) => setJobDetails(prev => ({...prev, isDeepClean: e.target.checked}))}
                  />
                  <span className="checkbox-label">
                    <Sparkles size={16} />
                    This is a deep clean / first visit
                  </span>
                </label>
              </div>
            </div>

            {/* Pricing Card */}
            <div className="section-card">
              <div className="section-header-inline">
                <h3><DollarSign size={18} /> Pricing</h3>
                <button className="btn btn-sm btn-outline" onClick={() => setShowAddItem(true)}>
                  <Plus size={14} /> Add Item
                </button>
              </div>

              <div className="line-items-list">
                {lineItems.map(item => (
                  <div key={item.id} className="line-item-row">
                    <div className="item-details">
                      <span className="item-label">{item.label}</span>
                      {item.description && <span className="item-desc">{item.description}</span>}
                    </div>
                    <div className="item-price-input">
                      <span>$</span>
                      <input
                        type="number"
                        value={item.price}
                        onChange={(e) => updateLineItemPrice(item.id, e.target.value)}
                        step="5"
                        min="0"
                      />
                    </div>
                    {!item.required && (
                      <button 
                        className="btn-remove"
                        onClick={() => removeLineItem(item.id)}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="price-total">
                <span>Total</span>
                <span className="total-amount">{formatCurrency(totalPrice)}</span>
              </div>
            </div>

            {/* Team Card */}
            <div className="section-card">
              <h3><User size={18} /> Assign Team (Optional)</h3>
              
              <div className="team-grid">
                {activeEmployees.map(emp => (
                  <button
                    key={emp.id}
                    className={`team-member ${jobDetails.team.includes(emp.id) ? 'selected' : ''}`}
                    onClick={() => toggleTeamMember(emp.id)}
                  >
                    <div className="member-avatar">
                      {emp.firstName.charAt(0)}{emp.lastName.charAt(0)}
                    </div>
                    <span>{emp.firstName} {emp.lastName.charAt(0)}.</span>
                    {jobDetails.team.includes(emp.id) && <Check size={14} />}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes Card */}
            <div className="section-card">
              <h3>Notes</h3>
              <textarea
                value={jobDetails.notes}
                onChange={(e) => setJobDetails(prev => ({...prev, notes: e.target.value}))}
                placeholder="Any special instructions or notes..."
                rows={3}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="page-actions">
            <div className="checkbox-option inline">
              <label>
                <input
                  type="checkbox"
                  checked={jobDetails.createEstimate}
                  onChange={(e) => setJobDetails(prev => ({...prev, createEstimate: e.target.checked}))}
                />
                <span>Auto-create approved estimate for reference</span>
              </label>
            </div>
            
            <div className="action-buttons">
              <button className="btn btn-secondary" onClick={() => navigate('/admin/dispatch')}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveJob}>
                <Save size={18} />
                Add to Dispatch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Client Modal */}
      <Modal
        isOpen={showNewClientForm}
        onClose={() => setShowNewClientForm(false)}
        title="New Client"
      >
        <form onSubmit={handleCreateClient} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                value={newClient.firstName}
                onChange={(e) => setNewClient({...newClient, firstName: e.target.value})}
                required
                placeholder="Jane"
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                value={newClient.lastName}
                onChange={(e) => setNewClient({...newClient, lastName: e.target.value})}
                required
                placeholder="Smith"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={newClient.email}
              onChange={(e) => setNewClient({...newClient, email: e.target.value})}
              placeholder="jane@email.com"
            />
          </div>
          
          <div className="form-group">
            <label>Phone</label>
            <input
              type="tel"
              value={newClient.phone}
              onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
              placeholder="(434) 555-1234"
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowNewClientForm(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Create & Continue
            </button>
          </div>
        </form>
      </Modal>

      {/* New Property Modal */}
      <Modal
        isOpen={showNewPropertyForm}
        onClose={() => setShowNewPropertyForm(false)}
        title="Add Property"
      >
        <form onSubmit={handleCreateProperty} className="modal-form">
          <div className="form-group">
            <label>Street Address *</label>
            <input
              type="text"
              value={newProperty.addressLine1}
              onChange={(e) => setNewProperty({...newProperty, addressLine1: e.target.value})}
              required
              placeholder="123 Main Street"
            />
          </div>
          
          <div className="form-group">
            <label>Apt/Suite</label>
            <input
              type="text"
              value={newProperty.addressLine2}
              onChange={(e) => setNewProperty({...newProperty, addressLine2: e.target.value})}
              placeholder="Apt 2B"
            />
          </div>

          <div className="form-row thirds">
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                value={newProperty.city}
                onChange={(e) => setNewProperty({...newProperty, city: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>State</label>
              <input
                type="text"
                value={newProperty.state}
                onChange={(e) => setNewProperty({...newProperty, state: e.target.value})}
                maxLength={2}
              />
            </div>
            <div className="form-group">
              <label>ZIP *</label>
              <input
                type="text"
                value={newProperty.zip}
                onChange={(e) => setNewProperty({...newProperty, zip: e.target.value})}
                required
                placeholder="22901"
              />
            </div>
          </div>

          <div className="form-row thirds">
            <div className="form-group">
              <label>Square Feet</label>
              <input
                type="number"
                value={newProperty.sqft}
                onChange={(e) => setNewProperty({...newProperty, sqft: e.target.value})}
                placeholder="2200"
              />
            </div>
            <div className="form-group">
              <label>Bedrooms</label>
              <input
                type="number"
                value={newProperty.bedrooms}
                onChange={(e) => setNewProperty({...newProperty, bedrooms: e.target.value})}
                placeholder="3"
              />
            </div>
            <div className="form-group">
              <label>Bathrooms</label>
              <input
                type="number"
                value={newProperty.bathrooms}
                onChange={(e) => setNewProperty({...newProperty, bathrooms: e.target.value})}
                step="0.5"
                placeholder="2.5"
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowNewPropertyForm(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Add & Continue
            </button>
          </div>
        </form>
      </Modal>

      {/* Add Line Item Modal */}
      <Modal
        isOpen={showAddItem}
        onClose={() => setShowAddItem(false)}
        title="Add Line Item"
      >
        <div className="modal-form">
          <div className="form-group">
            <label>Description</label>
            <input
              type="text"
              value={newItem.label}
              onChange={(e) => setNewItem({...newItem, label: e.target.value})}
              placeholder="e.g., Inside Oven, Extra Bedroom, etc."
            />
          </div>
          
          <div className="form-group">
            <label>Price</label>
            <div className="currency-input">
              <span>$</span>
              <input
                type="number"
                value={newItem.price}
                onChange={(e) => setNewItem({...newItem, price: e.target.value})}
                placeholder="0"
                step="5"
                min="0"
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowAddItem(false)}>
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={handleAddLineItem}
              disabled={!newItem.label || !newItem.price}
            >
              Add Item
            </button>
          </div>
        </div>
      </Modal>

      {/* Duplicate Client Warning Modal */}
      <Modal
        isOpen={showDuplicateWarning}
        onClose={() => {
          setShowDuplicateWarning(false);
          setPendingClientData(null);
          setDuplicateClients([]);
        }}
        title="Possible Duplicate Client"
      >
        <div className="duplicate-warning-content">
          <div className="warning-header">
            <AlertTriangle size={24} className="warning-icon" />
            <p>We found existing client(s) that may be the same person:</p>
          </div>
          
          <div className="duplicate-clients-list">
            {duplicateClients.map(client => (
              <div key={client.id} className="duplicate-client-card">
                <div className="client-info">
                  <strong>{client.firstName} {client.lastName}</strong>
                  {client.email && <span className="client-detail">{client.email}</span>}
                  {client.phone && <span className="client-detail">{client.phone}</span>}
                </div>
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => handleUseExistingClient(client)}
                >
                  Use This Client
                </button>
              </div>
            ))}
          </div>

          <div className="warning-actions">
            <button 
              className="btn btn-secondary"
              onClick={() => {
                setShowDuplicateWarning(false);
                setPendingClientData(null);
                setDuplicateClients([]);
              }}
            >
              Cancel
            </button>
            <button 
              className="btn btn-warning"
              onClick={handleCreateAnyway}
            >
              Create New Anyway
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default QuickAddJob;
